import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FaEye, FaPrint, FaTimes, FaFileAlt, FaSearch, FaFlask } from "react-icons/fa";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../../components/PageHeading";
import parse from "html-react-parser";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const MyLabReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const printRef = useRef();
  const breadcrumbs = [{ label: "Dashboard", url: "/" }, { label: "My Reports" }];

  useEffect(() => {
    fetchMyReports();
  }, []);

const fetchMyReports = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      // 1. Get Logged-in User (MH_P_611951)
      const profileRes = await axios.get(`${BASE_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = profileRes.data;

      if (user.role === 'patient' || user.role === 'user') {
          const allPatientsRes = await axios.get(`${BASE_URL}/patient`, {
             headers: { "x-api-key": API_KEY } 
          });
          
          let targetPatient = null;
          
          if (Array.isArray(allPatientsRes.data)) {
             targetPatient = allPatientsRes.data.find(p => p.userId === user.id || p.id === user.id);
          } else if (allPatientsRes.data.patients) {
             targetPatient = allPatientsRes.data.patients.find(p => p.userId === user.id || p.id === user.id);
          }

          if (targetPatient) {
              const patientId = targetPatient.id;
              
              const reportRes = await axios.get(`${BASE_URL}/pathology/reports/patient/${patientId}`, {
                headers: { "x-api-key": API_KEY }
              });
              setReports(reportRes.data.reports || []);
          } else {
              console.warn("No linked patient profile found.");
              setReports([]);
          }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load your reports");
    } finally {
      setLoading(false);
    }
  };

  // Filter Logic
  const filteredReports = reports.filter(r => 
    r.template?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    new Date(r.createdAt).toLocaleDateString().includes(searchQuery)
  );

  // Pagination Logic
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const getPreviewOptions = (report) => ({
    replace: (domNode) => {
        if (domNode.type === "text" && domNode.data) {
            let text = domNode.data;
            if (text.includes("{{Name}}")) text = text.replace(/{{Name}}/g, report.patient?.name || "");
            if (text.includes("{{Age}}")) text = text.replace(/{{Age}}/g, report.patient?.age || ""); 
            if (text.includes("{{Reg}}")) text = text.replace(/{{Reg}}/g, report.patientId || "");
            if (text.includes("{{Ref}}")) text = text.replace(/{{Ref}}/g, report.referredBy || "Self");
            if (text.includes("{{Date}}")) text = text.replace(/{{Date}}/g, new Date(report.createdAt).toLocaleDateString("en-GB"));
            if (text.includes("__INPUT")) text = text.replace(/__INPUT:(.*?)__/g, '$1').replace(/__INPUT__/g, ''); 
            return <>{text}</>;
        }
        if (domNode.name === 'button' || (domNode.attribs && (domNode.attribs.class?.includes('add-row-btn') || domNode.attribs.class?.includes('delete-cell')))) return <></>;
        if (domNode.name === 'input') return <span className="font-medium text-gray-900">{domNode.attribs.value || ""}</span>;
    }
  });

  const handlePrint = () => window.print();

  return (
    <div>
      <ToastContainer position="top-right" className="print:hidden" />
      <div className="print:hidden"><PageHeading title="My Lab Reports" breadcrumbs={breadcrumbs} /></div>

      <div className="bg-white shadow-sm rounded-lg print:hidden mt-6">
        
        {/* Header */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-gray-200 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Report History
            </h2>
            <div className="relative w-full sm:w-64">
                <input 
                    type="text" 
                    placeholder="Search Test Name..." 
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs"/>
            </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
            <table className="min-w-[800px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">No.</th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">Date</th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">Test Name</th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">Referred By</th>
                        <th className="text-center uppercase font-inter font-medium text-sm text-gray-500 p-3">View</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {loading ? (
                        <tr><td colSpan="5" className="text-center py-8 text-gray-500">Loading...</td></tr>
                    ) : paginatedReports.length > 0 ? (
                        paginatedReports.map((report, i) => (
                            <tr key={report.id} className="hover:bg-blue-50/50 transition-colors border-b">
                                <td className="p-3 text-sm text-gray-700 font-inter">{(currentPage - 1) * itemsPerPage + i + 1}</td>
                                <td className="p-3 text-sm text-gray-500 font-inter">{new Date(report.createdAt).toLocaleDateString()}</td>
                                <td className="p-3 text-sm text-blue-600 font-medium font-inter">{report.template?.name}</td>
                                <td className="p-3 text-sm text-gray-500 font-inter">{report.referredBy || "Self"}</td>
                                <td className="p-3 text-center">
                                    <button 
                                        onClick={() => setSelectedReport(report)}
                                        className="size-7 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-all inline-flex items-center justify-center"
                                        title="View Report"
                                    >
                                        <FaEye size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5" className="text-center py-12 text-gray-400 font-inter text-sm">
                                <div className="flex flex-col items-center gap-2">
                                    <FaFlask className="text-gray-200 text-4xl" />
                                    <span>No reports found.</span>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
            <div className="p-4 flex items-center justify-between border-t border-gray-200">
                <p className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredReports.length)} of {filteredReports.length} entries
                </p>
                <div className="flex items-center gap-1">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"><MdKeyboardArrowLeft size={20} /></button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button key={i} onClick={() => handlePageChange(i + 1)} className={`w-7 h-7 rounded text-xs font-medium transition-colors ${currentPage === i + 1 ? "bg-blue-600 text-white" : "hover:bg-gray-100 text-gray-600"}`}>{i + 1}</button>
                    ))}
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"><MdKeyboardArrowRight size={20} /></button>
                </div>
            </div>
        )}
      </div>

      {/* --- PREVIEW MODAL --- */}
        {selectedReport && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 print:p-0 print:bg-white print:static print:block">
                <div className="bg-white w-full max-w-4xl h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden print:shadow-none print:h-auto print:w-full print:max-w-none print:rounded-none">
                  <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 print:hidden">
                    <h3 className="font-bold text-gray-700 text-lg flex items-center gap-2">
                      <FaFileAlt className="text-blue-600" /> Report Preview
                    </h3>
                    <div className="flex gap-3">
                      <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 bg-[#60b452] hover:bg-[#4a963e] text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md transition-all"
                      >
                        <FaPrint /> Print
                      </button>
                      <button
                        onClick={() => setSelectedReport(null)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      >
                        <FaTimes size={20} />
                      </button>
                    </div>
                  </div>
                  <div className="overflow-y-auto bg-gray-100 p-8 print:p-0 print:bg-white print:overflow-visible print:block">
                    <div
                      id="print-area"
                      className="shadow-lg print:shadow-none print:w-full"
                    >
                      <div
                        ref={printRef}
                        className="bg-white relative flex flex-col justify-between print:w-full print:min-h-0 print:mx-0 print:h-auto"
                      >
                        <div className="w-full h-full template-content">
                          {parse(
                            selectedReport.reportContent,
                            getPreviewOptions(selectedReport)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

      {/* STYLES */}
       <style>{`
        .header-gradient { background: linear-gradient(120deg, #3f54e7, #60b452) !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .footer-shape { content: ''; position: absolute; top: 0; right: 0; width: 150px; height: 100%; background-color: #60b452 !important; clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%); -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .print-bg-force { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

        /* 🔥 FIX FOR PRINTING */
        @media print { 
            @page { margin: 0; size: A4; }
            body * { visibility: hidden; }
            #print-area, #print-area * { visibility: visible; }
            #print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; background: white; }
            input::placeholder { color: transparent; }
            input { border-bottom: none !important; }
        }
      `}</style>
    </div>
  );
};

export default MyLabReports;